import { BirdviewSection } from '../DashboardBirdview.constants'
import {
  BirdviewAnalyticsPreset,
  BirdviewChartObject,
  BirdviewChartTone,
  BirdviewChartType,
  BirdviewCustomerSegment,
  BirdviewKpiUnit,
} from '../DashboardBirdview.constants.charts'
import {
  BIRDVIEW_AGING_BASIS_OPTIONS,
  BIRDVIEW_CHART_BUILDING_BARS_DEFAULT_LIMIT,
  BIRDVIEW_CHART_BUILDING_METRIC_OPTIONS,
  BIRDVIEW_CHART_CATEGORY_BARS_DEFAULT_LIMIT,
  BIRDVIEW_CHART_MANAGER_BARS_DEFAULT_LIMIT,
  BIRDVIEW_CHART_MANAGER_METRIC_OPTIONS,
  BIRDVIEW_CHART_PERIOD_OPTIONS,
} from '../DashboardBirdview.constants.controls'
import { QA_NAME_DASHBOARD_BIRDVIEW_PERIOD } from '../DashboardBirdview.constants.qa'

// The stored layout is keyed by these values, so renaming one resets it.
const BirdviewChartId = {
  BuildingsByCity: 'buildingsByCity',
  ContractsEnding: 'contractsEnding',
  CustomersByContact: 'customersByContact',
  CustomersByRole: 'customersByRole',
  CustomersByStatus: 'customersByStatus',
  DocFlowsByPhase: 'docFlowsByPhase',
  EmailsOverTime: 'emailsOverTime',
  OpenedVsClosed: 'openedVsClosed',
  RequestsAging: 'requestsAging',
  RequestsByBuilding: 'requestsByBuilding',
  RequestsByManager: 'requestsByManager',
  RequestsByProvider: 'requestsByProvider',
  RequestsByStatus: 'requestsByStatus',
  RequestsByType: 'requestsByType',
  UnansweredEmailsByAge: 'unansweredEmailsByAge',
  UnitsOccupancy: 'unitsOccupancy',
}

const BIRDVIEW_FLOW_CHART = {
  columnSpan: 2,
  id: BirdviewChartId.OpenedVsClosed,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartOpenedVsClosedQuestionKey',
  sectionKey: BirdviewSection.ChartOpenedVsClosed,
  titleKey: 'DashboardBirdview.ChartOpenedVsClosedTitleKey',
  type: BirdviewChartType.Line,
}

const BIRDVIEW_STATUS_CHART = {
  id: BirdviewChartId.RequestsByStatus,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartRequestsByStatusQuestionKey',
  sectionKey: BirdviewSection.ChartByStatus,
  titleKey: 'DashboardBirdview.ChartRequestsByStatusTitleKey',
  type: BirdviewChartType.Donut,
}

const BIRDVIEW_AGING_CHART = {
  emptyKey: 'DashboardBirdview.ChartAgingEmptyStateKey',
  id: BirdviewChartId.RequestsAging,
  isSnapshot: true,
  metricOptions: BIRDVIEW_AGING_BASIS_OPTIONS,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartAgingSlaQuestionKey',
  sectionKey: BirdviewSection.ChartAging,
  titleKey: 'DashboardBirdview.ChartAgingTitleKey',
  type: BirdviewChartType.Aging,
}

const BIRDVIEW_BUILDING_CHART = {
  color: BirdviewChartTone.Success,
  columnSpan: 2,
  defaultLimit: BIRDVIEW_CHART_BUILDING_BARS_DEFAULT_LIMIT,
  id: BirdviewChartId.RequestsByBuilding,
  metricOptions: BIRDVIEW_CHART_BUILDING_METRIC_OPTIONS,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartRequestsByBuildingQuestionKey',
  sectionKey: BirdviewSection.ChartByBuilding,
  titleKey: 'DashboardBirdview.ChartRequestsByBuildingTitleKey',
  type: BirdviewChartType.Bars,
}

const BIRDVIEW_CATEGORY_CHART = {
  color: BirdviewChartTone.Info,
  defaultLimit: BIRDVIEW_CHART_CATEGORY_BARS_DEFAULT_LIMIT,
  id: BirdviewChartId.RequestsByType,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartRequestsByTypeQuestionKey',
  sectionKey: BirdviewSection.ChartByType,
  titleKey: 'DashboardBirdview.ChartRequestsByTypeTitleKey',
  type: BirdviewChartType.Bars,
}

const BIRDVIEW_CUSTOMER_CHART = {
  asOfNowKey: 'DashboardBirdview.ChartAsOfNowIncludingArchivedKey',
  emptyKey: 'DashboardBirdview.ChartCustomersEmptyStateKey',
  isHiddenByDefault: true,
  isSnapshot: true,
  object: BirdviewChartObject.Customers,
  sectionKey: BirdviewSection.ChartCustomers,
  type: BirdviewChartType.Donut,
}

const BIRDVIEW_EMAILS_OVER_TIME_CHART = {
  id: BirdviewChartId.EmailsOverTime,
  object: BirdviewChartObject.Emails,
  questionKey: 'DashboardBirdview.ChartEmailsOverTimeQuestionKey',
  titleKey: 'DashboardBirdview.ChartEmailsOverTimeTitleKey',
}

const BIRDVIEW_UNANSWERED_EMAILS_CHART = {
  id: BirdviewChartId.UnansweredEmailsByAge,
  object: BirdviewChartObject.Emails,
  questionKey: 'DashboardBirdview.ChartUnansweredEmailsByAgeQuestionKey',
  titleKey: 'DashboardBirdview.ChartUnansweredEmailsByAgeTitleKey',
}

// The Portfolio ids are the insights tile keys.
export const BIRDVIEW_KPI_DEFINITIONS = {
  [BirdviewAnalyticsPreset.Personal]: [
    {
      id: 'resolvedThisWeek',
      labelKey: 'DashboardBirdview.KpiResolvedThisWeekKey',
      unit: BirdviewKpiUnit.Count,
    },
    {
      id: 'myAvgResolution',
      labelKey: 'DashboardBirdview.KpiMyAvgResolutionKey',
      unit: BirdviewKpiUnit.Days,
    },
    {
      id: 'onTime',
      labelKey: 'DashboardBirdview.KpiOnTimeKey',
      unit: BirdviewKpiUnit.Percent,
    },
  ],
  [BirdviewAnalyticsPreset.Portfolio]: [
    {
      id: 'avg_resolution',
      labelKey: 'DashboardBirdview.KpiAvgResolutionKey',
      unit: BirdviewKpiUnit.Days,
    },
    {
      id: 'on_time',
      labelKey: 'DashboardBirdview.KpiOnTimeKey',
      unit: BirdviewKpiUnit.Percent,
    },
    {
      id: 'backlog',
      labelKey: 'DashboardBirdview.TileBacklogKey',
      unit: BirdviewKpiUnit.Count,
    },
  ],
}

export const BIRDVIEW_ANALYTICS_PERIOD_OPTIONS =
  BIRDVIEW_CHART_PERIOD_OPTIONS.map(option => ({
    ...option,
    qaName: `${QA_NAME_DASHBOARD_BIRDVIEW_PERIOD}${option.days}`,
  }))

// A two-column lead chart makes the four-column grid read as three per row.
export const BIRDVIEW_ANALYTICS_CHARTS = {
  [BirdviewAnalyticsPreset.Personal]: [
    BIRDVIEW_FLOW_CHART,
    BIRDVIEW_STATUS_CHART,
    BIRDVIEW_AGING_CHART,
    BIRDVIEW_BUILDING_CHART,
    { ...BIRDVIEW_CATEGORY_CHART, columnSpan: 2 },
  ],
  [BirdviewAnalyticsPreset.Portfolio]: [
    BIRDVIEW_FLOW_CHART,
    BIRDVIEW_STATUS_CHART,
    BIRDVIEW_AGING_CHART,
    BIRDVIEW_BUILDING_CHART,
    BIRDVIEW_CATEGORY_CHART,
    {
      color: BirdviewChartTone.Info,
      defaultLimit: BIRDVIEW_CHART_MANAGER_BARS_DEFAULT_LIMIT,
      id: BirdviewChartId.RequestsByManager,
      isStacked: true,
      metricOptions: BIRDVIEW_CHART_MANAGER_METRIC_OPTIONS,
      noteKey: 'DashboardBirdview.ChartByManagerCountingNoteKey',
      object: BirdviewChartObject.Requests,
      questionKey: 'DashboardBirdview.ChartRequestsByManagerQuestionKey',
      sectionKey: BirdviewSection.ChartByManager,
      titleKey: 'DashboardBirdview.ChartRequestsByManagerTitleKey',
      type: BirdviewChartType.Bars,
    },
    {
      ...BIRDVIEW_CUSTOMER_CHART,
      columnSpan: 2,
      dataKey: BirdviewCustomerSegment.Role,
      id: BirdviewChartId.CustomersByRole,
      questionKey: 'DashboardBirdview.ChartCustomersByRoleQuestionKey',
      titleKey: 'DashboardBirdview.ChartCustomersByRoleTitleKey',
    },
    {
      ...BIRDVIEW_CUSTOMER_CHART,
      dataKey: BirdviewCustomerSegment.Status,
      id: BirdviewChartId.CustomersByStatus,
      questionKey: 'DashboardBirdview.ChartCustomersByStatusQuestionKey',
      titleKey: 'DashboardBirdview.ChartCustomersByStatusTitleKey',
    },
    {
      ...BIRDVIEW_CUSTOMER_CHART,
      dataKey: BirdviewCustomerSegment.Contact,
      id: BirdviewChartId.CustomersByContact,
      questionKey: 'DashboardBirdview.ChartCustomersByContactQuestionKey',
      titleKey: 'DashboardBirdview.ChartCustomersByContactTitleKey',
    },
  ],
}

// Part 2 of the catalogue: listed ahead of their data, so they cannot be added.
export const BIRDVIEW_ANALYTICS_SOON_CHARTS = {
  [BirdviewAnalyticsPreset.Personal]: [
    BIRDVIEW_EMAILS_OVER_TIME_CHART,
    BIRDVIEW_UNANSWERED_EMAILS_CHART,
  ],
  [BirdviewAnalyticsPreset.Portfolio]: [
    {
      id: BirdviewChartId.RequestsByProvider,
      object: BirdviewChartObject.Requests,
      questionKey: 'DashboardBirdview.ChartRequestsByProviderQuestionKey',
      titleKey: 'DashboardBirdview.ChartRequestsByProviderTitleKey',
    },
    {
      id: BirdviewChartId.BuildingsByCity,
      object: BirdviewChartObject.Buildings,
      questionKey: 'DashboardBirdview.ChartBuildingsByCityQuestionKey',
      titleKey: 'DashboardBirdview.ChartBuildingsByCityTitleKey',
    },
    {
      id: BirdviewChartId.UnitsOccupancy,
      object: BirdviewChartObject.Units,
      questionKey: 'DashboardBirdview.ChartUnitsOccupancyQuestionKey',
      titleKey: 'DashboardBirdview.ChartUnitsOccupancyTitleKey',
    },
    {
      id: BirdviewChartId.ContractsEnding,
      object: BirdviewChartObject.Units,
      questionKey: 'DashboardBirdview.ChartContractsEndingQuestionKey',
      titleKey: 'DashboardBirdview.ChartContractsEndingTitleKey',
    },
    BIRDVIEW_EMAILS_OVER_TIME_CHART,
    BIRDVIEW_UNANSWERED_EMAILS_CHART,
    {
      id: BirdviewChartId.DocFlowsByPhase,
      object: BirdviewChartObject.DocFlows,
      questionKey: 'DashboardBirdview.ChartDocFlowsByPhaseQuestionKey',
      titleKey: 'DashboardBirdview.ChartDocFlowsByPhaseTitleKey',
    },
  ],
}

export const BIRDVIEW_ANALYTICS_TILES_PER_ROW = 3
